import React from "react";
import { Link } from "react-router-dom";
import { LegalPage, Part, List, Mail, Phone, COMPANY } from "@/components/Legal";

// Public Terms of Service (dentmemo.in/terms). Keep the URL stable. Update UPDATED whenever the text changes.

const UPDATED = "3 November 2026";
const A = ({ to, children }) => <Link to={to} className="font-semibold text-slate-900 underline underline-offset-2">{children}</Link>;

export default function Terms() {
  return (
    <LegalPage title="Terms of Service" updated={UPDATED} testId="terms-of-service">
      <p className="mt-6">
        These terms are an agreement between you and {COMPANY.name} (LLPIN {COMPANY.llpin}), registered office at {COMPANY.address}
        (“DentMemo”, “we”, “us”). They cover your use of the website dentmemo.in and the DentMemo app. By creating an account or using
        DentMemo, you agree to these terms and to our <A to="/privacy">Privacy Policy</A>.
      </p>

      <Part title="The service">
        <p>
          DentMemo is record-keeping software for dental clinics: patient records, visits, appointments, consultant payouts and reports.
          It is a tool for organising records. It does not give medical advice, and clinical decisions remain the responsibility of the
          treating dentist.
        </p>
      </Part>

      <Part title="Accounts">
        <List items={[
          ["Who can sign up:", "DentMemo is for adults working at a dental clinic. The person who creates a clinic is its owner and may invite staff."],
          ["Accurate details:", "keep your account details, including your mobile number, accurate and up to date."],
          ["Security:", "keep your password private and only sign in on devices your clinic controls. You are responsible for activity under your account; tell us at once if you suspect misuse."],
          ["Owners and staff:", "the clinic owner controls who has access to the clinic and is responsible for the actions of its staff in DentMemo."],
        ]} />
      </Part>

      <Part title="Your clinic’s data">
        <p>
          The records your clinic enters, including patient records, belong to your clinic. You give us permission to store and process
          them only to provide DentMemo to you. The clinic is responsible for having a lawful basis, such as patient consent, for what it
          records, and for meeting any legal duty to keep patient records. You can export or delete your data at any time; see{" "}
          <A to="/data-deletion">Data Deletion</A>.
        </p>
      </Part>

      <Part title="Free trial, plans and payment">
        <List items={[
          ["Free trial:", "each new clinic gets a 30-day free trial. No card is needed to start."],
          ["Plans:", "after the trial, DentMemo is a paid subscription, billed monthly or annually at the prices shown on the Billing page, in Indian rupees and inclusive of applicable taxes unless stated otherwise."],
          ["Payment:", "subscriptions are processed by Razorpay and renew automatically at the end of each billing period until cancelled. We never see or store your card, UPI or bank details."],
          ["Price changes:", "we will tell clinic owners before a price change applies to their next renewal."],
        ]} />
      </Part>

      <Part title="Cancellation and refunds">
        <p>
          You can cancel at any time from the Billing page. Cancelling stops future renewals; you keep access until the end of the period
          you have already paid for. Payments already made are not refunded, except where the law requires it or where you were charged in
          error. If you think you were charged incorrectly, email <Mail /> within 30 days of the charge and we will look into it; approved
          refunds are returned to the original payment method within 7–10 working days.
        </p>
      </Part>

      <Part title="Acceptable use">
        <p>You agree not to:</p>
        <List items={[
          ["Break the law:", "use DentMemo for anything unlawful, or record data you have no right to hold."],
          ["Misuse access:", "access another clinic’s data, share your login, or try to get around access controls or role limits."],
          ["Harm the service:", "attack, overload, scrape or reverse-engineer DentMemo, or upload malicious files."],
        ]} />
        <p>We may suspend or close accounts that break these rules, after notice where reasonable.</p>
      </Part>

      <Part title="Availability and changes">
        <p>
          We work to keep DentMemo available and your data backed up, but the service is provided “as is” and we cannot promise it will be
          uninterrupted or error-free. Keep your own backups using the export in Settings. We may improve or change features over time, and
          will give notice before removing anything significant you rely on.
        </p>
      </Part>

      <Part title="Liability">
        <p>
          To the extent the law allows, we are not liable for indirect or consequential losses, such as lost profits or lost data, arising
          from your use of DentMemo. Our total liability for any claim is limited to the amount you paid us for DentMemo in the 12 months
          before the claim. Nothing in these terms limits liability that cannot be limited by law.
        </p>
      </Part>

      <Part title="Ending your use">
        <p>
          You can stop using DentMemo at any time and delete your data as described on the <A to="/data-deletion">Data Deletion</A> page.
          If an account is closed, you can still ask us for an export of your clinic’s data for 30 days afterwards, unless it has already
          been deleted.
        </p>
      </Part>

      <Part title="Changes to these terms">
        <p>
          If we change these terms we will update the date at the top of this page and tell clinic owners in the app or by email before any
          significant change takes effect. Continuing to use DentMemo after that means you accept the updated terms.
        </p>
      </Part>

      <Part title="Governing law">
        <p>
          These terms are governed by the laws of India. Any dispute will be subject to the exclusive jurisdiction of the courts at
          Kasaragod, Kerala.
        </p>
      </Part>

      <Part title="Contact">
        <p>Questions about these terms: <Mail /> or <Phone />, or write to {COMPANY.name}, {COMPANY.address}.</p>
      </Part>
    </LegalPage>
  );
}
